/**
 * One look for single pads (DEC-317 / DEC-321, operator: "6 yes 7 yes" for one look on single buttons and Helix tiles;
 * built 2026-10-27): a single pad (FireButton, a Helix preset or snapshot tile, a Rig State button, an OPVisual trigger)
 * takes the button grid's Finish, icon and texture. Each pad spreads `padAttrs(item)` on its `.midi-button` and renders
 * `<PadDecor item={item} />` inside it; the CSS is skin-components.css 12c/12d, shared with the grid's pads.
 *
 * On a single pad there is no title band, so the icon sits Above the name (the default once an icon is picked), in a
 * Corner, or Off.
 */

import { Show } from 'solid-js';
import type { FaceOptions } from '../layout/projectStore.ts';
import { HUE_VALUES } from '../layout/projectStore.ts';
import { InstrumentIcon } from './InstrumentIcon.tsx';
import { LOOK_DEFAULTS, textureFor, texturePx } from '../layout/buttonLook.ts';

type Pad = FaceOptions & { w: number; h: number };

const icon = (it: Pad) => (it.icon && it.icon !== 'none' ? it.icon : undefined);
const iconAt = (it: Pad) => {
  if (!icon(it)) return 'off';
  const a = it.iconAt ?? 'above';
  return a === 'title' ? 'above' : a;
};

/** The pad's attributes, for its `.midi-button`. */
export function padAttrs(it: Pad): { 'data-finish': string; 'data-icon-at': string } {
  return { 'data-finish': it.finish ?? LOOK_DEFAULTS.finish, 'data-icon-at': iconAt(it) };
}

export function PadDecor(props: { item: Pad }) {
  const tex = () => textureFor(icon(props.item) ?? 'none', props.item.texture, props.item.texSize);
  const px = () => { const t = tex(); return t.pattern === 'smooth' ? 0 : texturePx(Math.min(props.item.w, props.item.h), t.pattern, t.size); };
  const tc = () => props.item.texColour ?? 'auto';
  return (
    <>
      <Show when={px() > 0}>
        <span
          class="bg-cell-tx"
          aria-hidden="true"
          data-tx={tex().pattern}
          data-tc={tc() === 'auto' ? undefined : tc() === 'dark' ? 'dark' : 'hue'}
          style={{
            '--txs': `${px()}px`,
            '--txo': ((props.item.texStrength ?? LOOK_DEFAULTS.texStrength) / 100).toFixed(2),
            ...(tc() !== 'auto' && tc() !== 'dark' ? { '--tc': HUE_VALUES[tc() as keyof typeof HUE_VALUES] } : {}),
          }}
        >
          <i class="s" /><i class="h" />
        </span>
      </Show>
      <Show when={iconAt(props.item) !== 'off' && icon(props.item)}>
        <InstrumentIcon
          id={icon(props.item)!}
          class={`pad-icon${iconAt(props.item) === 'corner' ? ' pad-icon-corner' : ''}`}
        />
      </Show>
    </>
  );
}
